import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  FiAlertTriangle,
  FiPackage,
  FiArrowRight,
  FiPlusCircle,
  FiRefreshCw,
  FiLayers,
} from "react-icons/fi";
import escalationService from "../services/escalationService";

const getStatusBadge = (status) => {
  const map = {
    submitted: { bg: "#e8f1f8", text: "#002747", border: "#b6d5ec", label: "Submitted" },
    under_review: { bg: "#fef3c7", text: "#b45309", border: "#fde68a", label: "Under Review" },
    action_taken: { bg: "#f3e8ff", text: "#6b21a8", border: "#e9d5ff", label: "Action Taken" },
    in_progress: { bg: "#e0f2fe", text: "#0369a1", border: "#bae6fd", label: "In Progress" },
    approved: { bg: "#ecfdf5", text: "#047857", border: "#a7f3d0", label: "Approved" },
    partially_approved: { bg: "#fef9c3", text: "#854d0e", border: "#fef08a", label: "Partially Approved" },
    rejected: { bg: "#fef2f2", text: "#b91c1c", border: "#fecaca", label: "Rejected" },
    resolved: { bg: "#f0fdf4", text: "#15803d", border: "#bbf7d0", label: "Resolved" },
    closed: { bg: "#f1f5f9", text: "#475569", border: "#cbd5e1", label: "Closed" },
  };
  const config = map[status] || { bg: "#f1f5f9", text: "#475569", border: "#cbd5e1", label: status };
  return (
    <span
      style={{
        fontSize: "11px",
        fontWeight: 600,
        padding: "3px 8px",
        borderRadius: "999px",
        backgroundColor: config.bg,
        color: config.text,
        border: `1px solid ${config.border}`,
        whiteSpace: "nowrap",
        display: "inline-flex",
        alignItems: "center",
        gap: "4px",
      }}
    >
      <span
        style={{
          width: 6,
          height: 6,
          borderRadius: "50%",
          backgroundColor: config.text,
        }}
      />
      {config.label}
    </span>
  );
};

const getPriorityBadge = (priority) => {
  const map = {
    urgent: { bg: "#fee2e2", text: "#991b1b", label: "Urgent" },
    high: { bg: "#ffedd5", text: "#9a3412", label: "High" },
    medium: { bg: "#e0f2fe", text: "#075985", label: "Medium" },
    low: { bg: "#f1f5f9", text: "#475569", label: "Low" },
  };
  const c = map[priority] || map.medium;
  return (
    <span
      style={{
        fontSize: "10px",
        fontWeight: 700,
        textTransform: "uppercase",
        padding: "2px 6px",
        borderRadius: "4px",
        backgroundColor: c.bg,
        color: c.text,
      }}
    >
      {c.label}
    </span>
  );
};

export default function EscalationWidget() {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [recent, setRecent] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchData = async () => {
    try {
      setLoading(true);
      const res = await escalationService.getStats();
      if (res) {
        setStats(res.stats || res.data?.stats || res.data || {});
        setRecent(res.recentRequests || res.data?.recentRequests || []);
      }
    } catch (err) {
      console.error("Failed to load escalation stats:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  return (
    <div
      className="card"
      style={{
        marginBottom: "1.25rem",
        border: "1px solid #e2e8f0",
        boxShadow: "0 1px 3px rgba(0,0,0,0.05)",
        borderRadius: "12px",
        overflow: "hidden",
      }}
    >
      {/* ── Header ── */}
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          flexWrap: "wrap",
          gap: "0.75rem",
          padding: "1rem 1.25rem",
          borderBottom: "1px solid var(--border, #f1f5f9)",
          backgroundColor: "#fafbfc",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: "0.6rem" }}>
          <div
            style={{
              width: 32,
              height: 32,
              borderRadius: "8px",
              background: "#6366f11a",
              color: "#4f46e5",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <FiLayers size={17} />
          </div>
          <div>
            <div style={{ fontSize: "14px", fontWeight: 700, color: "var(--text, #1e293b)" }}>
              Super Admin Escalations & Resource Requests
            </div>
            <div style={{ fontSize: "11.5px", color: "var(--text-muted, #64748b)" }}>
              Escalate unresolved matters or request equipment & resources from central administration
            </div>
          </div>
        </div>

        <div style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
          <button
            type="button"
            onClick={fetchData}
            title="Refresh"
            style={{
              background: "#fff",
              border: "1px solid #e2e8f0",
              borderRadius: "6px",
              padding: "5px 8px",
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              color: "#64748b",
            }}
          >
            <FiRefreshCw size={13} className={loading ? "spin" : ""} />
          </button>
          <Link
            to="/escalations/new"
            className="btn btn-primary"
            style={{
              fontSize: "12px",
              padding: "5px 12px",
              display: "flex",
              alignItems: "center",
              gap: "4px",
              borderRadius: "6px",
            }}
          >
            <FiPlusCircle size={13} /> New Escalation / Request
          </Link>
          <Link
            to="/escalations"
            className="btn btn-outline"
            style={{
              fontSize: "12px",
              padding: "5px 10px",
              display: "flex",
              alignItems: "center",
              gap: "4px",
              borderRadius: "6px",
            }}
          >
            View All <FiArrowRight size={13} />
          </Link>
        </div>
      </div>

      {/* ── Content ── */}
      <div style={{ padding: "1.1rem 1.25rem" }}>
        {/* Stat Pills */}
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(130px, 1fr))",
            gap: "0.75rem",
            marginBottom: "1rem",
          }}
        >
          {/* Total Submitted / Pending */}
          <div
            style={{
              padding: "0.6rem 0.8rem",
              borderRadius: "8px",
              backgroundColor: "#e8f1f8",
              border: "1px solid #e8f1f8",
            }}
          >
            <div style={{ fontSize: "11px", fontWeight: 600, color: "#002747", textTransform: "uppercase" }}>
              Pending Review
            </div>
            <div style={{ fontSize: "20px", fontWeight: 800, color: "#1e3a8a", marginTop: "2px" }}>
              {loading ? "..." : stats?.pending || 0}
            </div>
          </div>

          {/* In Action / In Progress */}
          <div
            style={{
              padding: "0.6rem 0.8rem",
              borderRadius: "8px",
              backgroundColor: "#f5f3ff",
              border: "1px solid #ede9fe",
            }}
          >
            <div style={{ fontSize: "11px", fontWeight: 600, color: "#6d28d9", textTransform: "uppercase" }}>
              In Action / Progress
            </div>
            <div style={{ fontSize: "20px", fontWeight: 800, color: "#4c1d95", marginTop: "2px" }}>
              {loading ? "..." : stats?.inAction || 0}
            </div>
          </div>

          {/* Approved / Resolved */}
          <div
            style={{
              padding: "0.6rem 0.8rem",
              borderRadius: "8px",
              backgroundColor: "#ecfdf5",
              border: "1px solid #d1fae5",
            }}
          >
            <div style={{ fontSize: "11px", fontWeight: 600, color: "#047857", textTransform: "uppercase" }}>
              Resolved / Approved
            </div>
            <div style={{ fontSize: "20px", fontWeight: 800, color: "#064e3b", marginTop: "2px" }}>
              {loading ? "..." : stats?.resolved || 0}
            </div>
          </div>

          {/* Urgent Items */}
          <div
            style={{
              padding: "0.6rem 0.8rem",
              borderRadius: "8px",
              backgroundColor: "#fef2f2",
              border: "1px solid #fee2e2",
            }}
          >
            <div style={{ fontSize: "11px", fontWeight: 600, color: "#b91c1c", textTransform: "uppercase" }}>
              Urgent Matters
            </div>
            <div style={{ fontSize: "20px", fontWeight: 800, color: "#7f1d1d", marginTop: "2px" }}>
              {loading ? "..." : stats?.urgent || 0}
            </div>
          </div>
        </div>

        {/* Recent Items List */}
        {loading ? (
          <div style={{ textAlign: "center", padding: "1.5rem", color: "#64748b", fontSize: "13px" }}>
            Loading requests…
          </div>
        ) : recent.length === 0 ? (
          <div
            style={{
              textAlign: "center",
              padding: "1.5rem 1rem",
              backgroundColor: "#f8fafc",
              borderRadius: "8px",
              border: "1px dashed #cbd5e1",
            }}
          >
            <FiPackage size={28} color="#94a3b8" style={{ marginBottom: "6px" }} />
            <div style={{ fontSize: "13px", fontWeight: 600, color: "#334155" }}>
              No active escalations or resource requests
            </div>
            <div style={{ fontSize: "12px", color: "#64748b", marginTop: "2px", marginBottom: "0.75rem" }}>
              Need central approval, equipment, or assistance from the Super Admin?
            </div>
            <Link to="/escalations/new" className="btn btn-sm btn-primary">
              <FiPlusCircle size={12} /> Submit First Request
            </Link>
          </div>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: "0.5rem" }}>
            {recent.map((item) => (
              <div
                key={item._id}
                onClick={() => navigate(`/escalations/${item._id}`)}
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  padding: "0.75rem 0.9rem",
                  backgroundColor: "#fff",
                  border: "1px solid #e2e8f0",
                  borderRadius: "8px",
                  cursor: "pointer",
                  transition: "all 0.15s ease",
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.borderColor = "#6366f1";
                  e.currentTarget.style.boxShadow = "0 2px 6px rgba(99,102,241,0.08)";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.borderColor = "#e2e8f0";
                  e.currentTarget.style.boxShadow = "none";
                }}
              >
                <div style={{ display: "flex", alignItems: "center", gap: "0.75rem", minWidth: 0 }}>
                  <div
                    style={{
                      width: 32,
                      height: 32,
                      borderRadius: "6px",
                      backgroundColor: item.type === "escalation" ? "#fef3c7" : "#e0e7ff",
                      color: item.type === "escalation" ? "#b45309" : "#4338ca",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      flexShrink: 0,
                    }}
                  >
                    {item.type === "escalation" ? <FiAlertTriangle size={16} /> : <FiPackage size={16} />}
                  </div>

                  <div style={{ minWidth: 0 }}>
                    <div style={{ display: "flex", alignItems: "center", gap: "6px", flexWrap: "wrap" }}>
                      <span
                        style={{
                          fontSize: "11px",
                          fontFamily: "monospace",
                          fontWeight: 700,
                          color: "#475569",
                        }}
                      >
                        {item.ticketNumber || "ESC"}
                      </span>
                      {getPriorityBadge(item.priority)}
                      <span style={{ fontSize: "11px", color: "#94a3b8" }}>&bull;</span>
                      <span style={{ fontSize: "11px", color: "#64748b", fontWeight: 500 }}>
                        {item.category}
                      </span>
                    </div>

                    <div
                      style={{
                        fontSize: "13px",
                        fontWeight: 600,
                        color: "#1e293b",
                        marginTop: "2px",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {item.title}
                    </div>

                    {item.superAdminAction?.actionTaken && (
                      <div
                        style={{
                          fontSize: "11.5px",
                          color: "#4f46e5",
                          marginTop: "2px",
                          display: "flex",
                          alignItems: "center",
                          gap: "4px",
                        }}
                      >
                        <span style={{ fontWeight: 600 }}>Super Admin:</span>
                        <span style={{ color: "#334155" }}>{item.superAdminAction.actionTaken}</span>
                      </div>
                    )}
                  </div>
                </div>

                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "flex-end",
                    gap: "4px",
                    flexShrink: 0,
                    marginLeft: "0.75rem",
                  }}
                >
                  {getStatusBadge(item.status)}
                  <span style={{ fontSize: "11px", color: "#94a3b8" }}>
                    {new Date(item.createdAt).toLocaleDateString("en-IN", {
                      day: "numeric",
                      month: "short",
                    })}
                  </span>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
